import {
  AddressMode,
  FacilityContactMode,
  FacilityMode,
  NewAddressInput,
  NewFacilityContactInput,
  NewFacilityInput,
} from "./types";

export type RequestCreateValidationInput = {
  clientProfileId?: string | null;
  addressMode: AddressMode;
  selectedAddressId: string;
  newAddress: NewAddressInput;
  categoryId: string;
  otherCategoryText: string;
  startDate: string;
};

export type RequestCreateValidationErrors = {
  homeowner?: string;
  address?: string;
  category?: string;
  startDate?: string;
  newAddress?: Partial<Record<keyof Pick<
    NewAddressInput,
    "address" | "formattedAddress" | "city" | "state"
  >, string>>;
};

const hasText = (value?: string | null) => Boolean(value?.trim());

export const validateRequestCreate = ({
  clientProfileId,
  addressMode,
  selectedAddressId,
  newAddress,
  categoryId,
  otherCategoryText,
  startDate,
}: RequestCreateValidationInput): RequestCreateValidationErrors => {
  const errors: RequestCreateValidationErrors = {};

  if (!clientProfileId) {
    errors.homeowner = "Select a homeowner before creating the request.";
  }

  if (addressMode === "saved") {
    if (!selectedAddressId) {
      errors.address = "Choose a saved address or add a new one.";
    }
  } else {
    const newAddressErrors: RequestCreateValidationErrors["newAddress"] = {};

    if (!hasText(newAddress.address)) {
      newAddressErrors.address = "Enter the address line.";
    }

    if (!hasText(newAddress.formattedAddress)) {
      newAddressErrors.formattedAddress = "Enter the formatted address.";
    }

    if (!hasText(newAddress.city)) {
      newAddressErrors.city = "Enter the city.";
    }

    if (!hasText(newAddress.state)) {
      newAddressErrors.state = "Enter the state.";
    }

    if (Object.keys(newAddressErrors).length > 0) {
      errors.address = "Complete the new address.";
      errors.newAddress = newAddressErrors;
    }
  }

  if (!categoryId && !hasText(otherCategoryText)) {
    errors.category = "Choose a service category or enter a custom request type.";
  }

  if (!startDate) {
    errors.startDate = "Select a start date.";
  }

  return errors;
};

export const hasRequestCreateValidationErrors = (
  errors: RequestCreateValidationErrors
) =>
  Boolean(
    errors.homeowner ||
      errors.address ||
      errors.category ||
      errors.startDate ||
      Object.keys(errors.newAddress || {}).length
  );

export type OrganizationRequestCreateValidationInput = {
  clientProfileId?: string | null;
  facilityMode: FacilityMode;
  selectedFacilityId: string;
  newFacility: NewFacilityInput;
  facilityContactMode: FacilityContactMode;
  selectedFacilityContactId: string;
  newFacilityContact: NewFacilityContactInput;
  categoryId: string;
  otherCategoryText: string;
  startDate: string;
};

export type OrganizationRequestCreateValidationErrors = {
  organization?: string;
  facility?: string;
  facilityContact?: string;
  category?: string;
  startDate?: string;
  newFacility?: {
    name?: string;
    address?: Partial<Record<keyof Pick<
      NewAddressInput,
      "address" | "formattedAddress" | "city" | "state"
    >, string>>;
  };
  newFacilityContact?: Partial<Record<keyof NewFacilityContactInput, string>>;
};

export const validateOrganizationRequestCreate = ({
  clientProfileId,
  facilityMode,
  selectedFacilityId,
  newFacility,
  facilityContactMode,
  selectedFacilityContactId,
  newFacilityContact,
  categoryId,
  otherCategoryText,
  startDate,
}: OrganizationRequestCreateValidationInput): OrganizationRequestCreateValidationErrors => {
  const errors: OrganizationRequestCreateValidationErrors = {};

  if (!clientProfileId) {
    errors.organization = "Select an organization before creating the request.";
  }

  if (facilityMode === "existing") {
    if (!selectedFacilityId) {
      errors.facility = "Choose a facility or add a new one.";
    }
  } else {
    const facilityErrors: OrganizationRequestCreateValidationErrors["newFacility"] = {};
    const addressErrors: NonNullable<
      OrganizationRequestCreateValidationErrors["newFacility"]
    >["address"] = {};

    if (!hasText(newFacility.name)) {
      facilityErrors.name = "Enter the facility name.";
    }

    if (!hasText(newFacility.address.address)) {
      addressErrors.address = "Enter the address line.";
    }

    if (!hasText(newFacility.address.city)) {
      addressErrors.city = "Enter the city.";
    }

    if (!hasText(newFacility.address.state)) {
      addressErrors.state = "Enter the state.";
    }

    if (!hasText(newFacility.address.formattedAddress)) {
      addressErrors.formattedAddress = "Enter the formatted address.";
    }

    if (Object.keys(addressErrors).length > 0) {
      facilityErrors.address = addressErrors;
    }

    if (Object.keys(facilityErrors).length > 0) {
      errors.facility = "Complete the new facility.";
      errors.newFacility = facilityErrors;
    }
  }

  if (facilityContactMode === "existing") {
    if (!selectedFacilityContactId) {
      errors.facilityContact = "Choose a facility contact or add a new one.";
    }
  } else {
    const contactErrors: OrganizationRequestCreateValidationErrors["newFacilityContact"] = {};

    if (!hasText(newFacilityContact.name)) {
      contactErrors.name = "Enter the contact name.";
    }

    if (!hasText(newFacilityContact.phoneNumber)) {
      contactErrors.phoneNumber = "Enter the contact phone number.";
    }

    if (Object.keys(contactErrors).length > 0) {
      errors.facilityContact = "Complete the new facility contact.";
      errors.newFacilityContact = contactErrors;
    }
  }

  if (!categoryId && !hasText(otherCategoryText)) {
    errors.category = "Choose a service category or enter a custom request type.";
  }

  if (!startDate) {
    errors.startDate = "Select a start date.";
  }

  return errors;
};

export const hasOrganizationRequestCreateValidationErrors = (
  errors: OrganizationRequestCreateValidationErrors
) =>
  Boolean(
    errors.organization ||
      errors.facility ||
      errors.facilityContact ||
      errors.category ||
      errors.startDate ||
      Object.keys(errors.newFacility || {}).length ||
      Object.keys(errors.newFacility?.address || {}).length ||
      Object.keys(errors.newFacilityContact || {}).length
  );
